import React, { useState, useEffect } from 'react';
import '../styles/design-system.css';

export default function TravelWarnings() {
  const [warnings, setWarnings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Simulate loading travel warnings
    setTimeout(() => {
      setWarnings([
        {
          id: 1,
          country: 'Thailand',
          level: 'medium',
          title: 'Political Unrest',
          description: 'Recent political demonstrations in Bangkok. Avoid large gatherings and government buildings.',
          date: '2024-02-15',
          category: 'political'
        },
        {
          id: 2,
          country: 'Mexico',
          level: 'high',
          title: 'Hurricane Season',
          description: 'Active hurricane season affecting coastal areas. Check weather updates before travel.',
          date: '2024-02-14',
          category: 'weather'
        },
        {
          id: 3,
          country: 'France',
          level: 'low',
          title: 'Transport Strikes',
          description: 'Occasional transport strikes affecting public transportation in major cities.',
          date: '2024-02-13',
          category: 'transport'
        },
        {
          id: 4,
          country: 'Japan',
          level: 'medium',
          title: 'Earthquake Activity',
          description: 'Increased seismic activity in certain regions. Follow local emergency procedures.',
          date: '2024-02-12',
          category: 'natural'
        }
      ]);
      setLoading(false);
    }, 1000);
  }, []);

  const getWarningColor = (level) => {
    switch (level) {
      case 'high': return 'bg-red-100 border-red-400 text-red-800';
      case 'medium': return 'bg-yellow-100 border-yellow-400 text-yellow-800';
      case 'low': return 'bg-blue-100 border-blue-400 text-blue-800';
      default: return 'bg-gray-100 border-gray-400 text-gray-800';
    }
  };

  const getWarningIcon = (level) => {
    switch (level) {
      case 'high': return '🚨';
      case 'medium': return '⚠️';
      case 'low': return 'ℹ️';
      default: return '📢';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Loading travel warnings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="travel-warnings">
      <div className="card p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-red-600 rounded-full flex items-center justify-center">
            <span className="text-white text-lg">🚨</span>
          </div>
          <div>
            <h3 className="text-xl font-semibold">Travel Warnings & Safety Info</h3>
            <p className="text-gray-600">Stay informed about travel safety and current warnings</p>
          </div>
        </div>

        <div className="space-y-4">
          {warnings.map((warning) => (
            <div key={warning.id} className={`p-4 rounded-lg border-l-4 ${getWarningColor(warning.level)}`}>
              <div className="flex items-start gap-3">
                <span className="text-2xl">{getWarningIcon(warning.level)}</span>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h4 className="font-semibold">{warning.country}</h4>
                    <span className={`text-xs px-2 py-1 rounded ${
                      warning.level === 'high' ? 'bg-red-200 text-red-800' :
                      warning.level === 'medium' ? 'bg-yellow-200 text-yellow-800' :
                      'bg-blue-200 text-blue-800'
                    }`}>
                      {warning.level.toUpperCase()}
                    </span>
                  </div>
                  <h5 className="font-medium mb-1">{warning.title}</h5>
                  <p className="text-sm mb-2">{warning.description}</p>
                  <p className="text-xs opacity-75">Updated: {new Date(warning.date).toLocaleDateString()}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-6 p-4 bg-blue-50 rounded-lg">
          <h4 className="font-semibold text-blue-900 mb-2">Safety Tips:</h4>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• Always check travel warnings before booking</li>
            <li>• Register with your embassy when traveling abroad</li>
            <li>• Keep emergency contacts handy</li>
            <li>• Follow local safety guidelines</li>
            <li>• Stay informed about current events</li>
          </ul>
        </div>
      </div>
    </div>
  );
} 